<script setup lang="ts">
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import NProgress from 'nprogress';
import DeviceMap from '@/components/device/map/DeviceMap.vue';
import SliderSearchTabsBar from '@/components/_common/SliderSearchTabsBar.vue';
import MapControlPanel from '@/components/_common/navbar/MapControlPanel.vue';
import DeviceListView from '@/views/DeviceListView.vue';
import DeviceListBar from '@/components/device/list/DeviceListBar.vue';
import TrackersTabs from '@/components/device/list/TrackersTabs.vue';
import ThreeColumnLayout from '@/components/_common/ThreeColumnLayout.vue';
import ScoutMapSuggestionsBanner from '@/components/ai/ScoutMapSuggestionsBanner.vue';
import { getAccountDevices } from '@gpx-spa/queries';
import { useDevice } from '@/composables/useDevice';

const typeId = 'trackersTabs';

import { useSynchronizedUrlQuerySearchStore } from '@/composables/useSynchronizedUrlQuerySearchStore';
import { useSynchronizedDeviceStatusQuery } from '@/composables/useSynchronizedDeviceStatusQuery';

useSynchronizedUrlQuerySearchStore();
useSynchronizedDeviceStatusQuery();

import { useSynchronizedSidebars } from '@/composables/useSynchronizedSidebars';
useSynchronizedSidebars({ defaultOpenSidebar: 'right', persistScoutOpenState: true });

import { useSynchronizedSearchSlideBar } from '@/composables/useSynchronizedSearchSlideBar';
useSynchronizedSearchSlideBar(typeId);

import { useResetChatOnSidebarClose } from '@/composables/useResetChatOnSidebarClose';
useResetChatOnSidebarClose();

import { useResetChatOnAccountOrUserChange } from '@/composables/useResetChatOnAccountOrUserChange';
useResetChatOnAccountOrUserChange();

// Get accountId from store
const store = useStore();
const accountId = computed(() => store.getters['auth/accountId']);
const canSeeCancelledDevices = computed(
  () => store.getters['auth/canSeeCancelledDevices'],
);
const deviceStore = useDevice();

// Prepare query parameters
const defaultStatuses = 'active,pending,pending-renewal,pending-cancellation';
const subscriptionStatuses = computed(() => {
  return canSeeCancelledDevices.value
    ? `${defaultStatuses},cancelled,blocked`
    : defaultStatuses;
});

// Fetch every status once; tab switching is a pure client-side filter via useDevice.applyFilter.
const queryParams = computed(() => ({
  with_current_position: true,
  subscription_status: subscriptionStatuses.value,
  exclude_groups: true,
  device_status: 'active,provisioned,hidden',
}));

// Fetch devices using vue-query
const { data: devicesData, isLoading: isDevicesLoading } = getAccountDevices(
  accountId,
  queryParams,
);

// Update Pinia store when devices data changes
watch(
  devicesData,
  (newDevices) => {
    if (newDevices) {
      deviceStore.fillStoreDevicesFromResponse(
        deviceStore.devices,
        newDevices,
        true,
      );
      deviceStore.applyFilter();
    }
  },
  { immediate: true },
);

// Update loading state in Pinia store and show/hide NProgress
watch(
  isDevicesLoading,
  (isLoading) => {
    deviceStore.isLoading = isLoading;

    if (isLoading) {
      NProgress.start();
    } else {
      NProgress.done();
    }
  },
  { immediate: true },
);
</script>

<template>
  <ThreeColumnLayout>
    <template #leftTop>
      <SliderSearchTabsBar :typeId="typeId" placeholder="Search Trackers...">
        <template #tabs>
          <TrackersTabs />
        </template>
      </SliderSearchTabsBar>
    </template>
    <template #left>
      <DeviceListView />
    </template>
    <template #rightTop>
      <MapControlPanel>
        <template #filters>
          <DeviceListBar />
        </template>
      </MapControlPanel>
    </template>
    <template #right>
      <div class="relative size-full">
        <DeviceMap :view="`cluster`" :viewType="`deviceView`" />
        <ScoutMapSuggestionsBanner />
      </div>
    </template>
  </ThreeColumnLayout>
</template>
